/**
 * Notes from calls with the account. A box to add a note, and the newest
 * notes listed beside it, newest first.
 */

import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  SI_NOTE_MAX_LENGTH,
  SI_NOTES_SHOWN_LIMIT,
} from "@onsemble/shared/types/sales-insights";
import { useAccountNotes, useAddAccountNote } from "@/hooks/use-account-notes";
import { formatDateTime } from "@/lib/date-utils";

// Text keys for the notes section
const k = (name: string) => `pages.pcInsights.account.placeholders.${name}`;

// Show the characters-left count once this few remain
const COUNTER_FROM = 200;

export function Notes({ accountId }: { accountId: string }) {
  const { t } = useTranslation();
  // What the rep has typed so far
  const [text, setText] = useState("");
  // The newest notes, from the server
  const { data: notes, isLoading, error: loadError } = useAccountNotes(accountId);
  // Saving a note
  const addNote = useAddAccountNote(accountId);

  // Characters still allowed
  const left = SI_NOTE_MAX_LENGTH - text.length;
  // Nothing but spaces can't be saved
  const canSave = text.trim().length > 0 && !addNote.isPending;

  const save = () => {
    // Clear the box only once the server has the note
    addNote.mutate(text, { onSuccess: () => setText("") });
  };

  return (
    <section className="mt-12">
      <h2 className="m-0 text-si-xl font-semibold leading-[1.2] tracking-[-0.015em]">
        {t(k("notesTitle"))}
      </h2>

      <div className="mt-4 grid grid-cols-2 gap-12 max-md:grid-cols-1 max-md:gap-6">
        {/* Add a note */}
        <div>
          <label
            htmlFor="si-note-box"
            className="mb-2 block text-si-sm font-semibold text-si-ink-2"
          >
            {t(k("addNote"))}
          </label>
          <textarea
            id="si-note-box"
            value={text}
            onChange={(e) => setText(e.target.value)}
            maxLength={SI_NOTE_MAX_LENGTH}
            // Locked while a save is in progress
            disabled={addNote.isPending}
            placeholder={t(k("notePlaceholder"))}
            className="min-h-[84px] w-full resize-y rounded-si-md border border-si-line-strong bg-si-bg px-3.5 py-3 text-si-md placeholder:text-si-ink-3 disabled:opacity-60"
          />
          {/* Characters left, only near the limit */}
          {left <= COUNTER_FROM && (
            <p className="m-0 mt-1 text-si-sm text-si-ink-3">
              {t(k("noteCharsLeft"), { count: left })}
            </p>
          )}
          {/* Save failed: the typed text stays so nothing is lost */}
          {addNote.isError && (
            <p role="alert" className="m-0 mt-2 text-si-sm text-si-down">
              {t(k("noteSaveFailed"))}
            </p>
          )}
          <button
            type="button"
            onClick={save}
            disabled={!canSave}
            className="mt-4 inline-flex min-h-11 items-center rounded-full border border-si-line-strong px-5 py-2.5 text-si-md font-bold text-si-ink disabled:opacity-60"
          >
            {t(k("saveNote"))}
          </button>
        </div>

        {/* Past notes, newest first */}
        <div>
          {isLoading ? null : loadError ? (
            // List didn't load
            <p className="m-0 border-t border-si-line py-3.5 text-si-md text-si-ink-2">
              {t(k("notesLoadFailed"))}
            </p>
          ) : !notes?.length ? (
            // No notes on this account yet
            <p className="m-0 border-t border-si-line py-3.5 text-si-md text-si-ink-2">
              {t(k("noNotes"))}
            </p>
          ) : (
            <>
              <ul className="m-0 list-none p-0">
                {notes.map((note) => (
                  <li key={note.id} className="border-t border-si-line py-3.5">
                    {/* When it was written */}
                    <p className="m-0 text-si-sm text-si-ink-3">{formatDateTime(note.createdAt)}</p>
                    {/* The note itself, line breaks kept */}
                    <p className="m-0 mt-1 whitespace-pre-wrap break-words text-si-md">
                      {note.body}
                    </p>
                  </li>
                ))}
              </ul>
              {/* A full list may mean older notes are hidden */}
              {notes.length === SI_NOTES_SHOWN_LIMIT && (
                <p className="m-0 mt-2 text-si-sm text-si-ink-3">
                  {t(k("notesShowingRecent"), { count: SI_NOTES_SHOWN_LIMIT })}
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </section>
  );
}
